import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabaseAuth, supabase } from '../lib/supabaseClient';
import { useNotifications } from './Notifications';
import { useData } from '../providers/DataProvider';
import { Mail, Lock, User, ArrowRight, Sparkles, ShieldCheck, Timer, Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { push } = useNotifications ? useNotifications() : { push: () => {} };
  const navigate = useNavigate();
  const { hasSession, preferences } = useData ? useData() : { hasSession: false, preferences: {} };
  const rateLimitTimer = useRef(null);

  useEffect(() => {
    setMounted(true);
    return () => {
      if (rateLimitTimer.current) clearTimeout(rateLimitTimer.current);
    };
  }, []);

  useEffect(() => {
    if (hasSession) {
      const dest = (preferences?.defaultPage && typeof preferences.defaultPage === 'string') ? preferences.defaultPage : '/';
      navigate(dest);
    }
  }, [hasSession, preferences, navigate]);

  const handleRateLimit = (message) => {
    const waitMatch = message?.match(/wait (\d+) seconds?/i);
    if (waitMatch) {
      const seconds = parseInt(waitMatch[1]);
      setRateLimitInfo({ seconds, remaining: seconds });
      
      if (rateLimitTimer.current) clearTimeout(rateLimitTimer.current);
      
      const countdown = () => {
        setRateLimitInfo(prev => {
          if (!prev || prev.remaining <= 1) {
            return null;
          }
          return { ...prev, remaining: prev.remaining - 1 };
        });
      };
      
      for (let i = 1; i < seconds; i++) {
        setTimeout(countdown, i * 1000);
      }
      
      rateLimitTimer.current = setTimeout(() => {
        setRateLimitInfo(null);
      }, seconds * 1000);
    }
  };

  const doPasswordLogin = async (e) => {
    e?.preventDefault?.();
    if (rateLimitInfo) {
      push(`Por favor espera ${rateLimitInfo.remaining} segundos antes de intentar de nuevo`, { type: 'warning' });
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabaseAuth.signInWithPasswordIdentifier(identifier.trim(), password);
      if (error) throw error;
      push('Sesión iniciada', { type: 'success' });
      const dest = (preferences?.defaultPage && typeof preferences.defaultPage === 'string') ? preferences.defaultPage : '/';
      navigate(dest);
    } catch (err) {
      const msg = err?.message || 'No se pudo iniciar sesión';
      if (msg.toLowerCase().includes('rate limit') || msg.toLowerCase().includes('wait')) {
        handleRateLimit(msg);
      }
      push(msg, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const doGoogle = async () => {
    setLoading(true);
    try {
      await supabaseAuth.signInWithOAuth('google');
    } catch (err) {
      push(err?.message || 'No se pudo iniciar con Google', { type: 'error' });
      setLoading(false);
    }
  };

  const doOtp = async () => {
    setLoading(true);
    try {
      const id = identifier.trim();
      if (!id) throw new Error('Ingresa tu email o usuario');
      const { error } = await supabaseAuth.signInWithIdentifierOtp(id);
      if (error) throw error;
      push('Hemos enviado un enlace a tu correo', { type: 'success' });
    } catch (err) {
      push(err?.message || 'No se pudo enviar el enlace', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const doForgot = async () => {
    try {
      const id = identifier.trim();
      if (!id) throw new Error('Ingresa tu email o usuario');
      // resolve username -> email if needed
      let email = id;
      if (!id.includes('@')) {
        const { data, error } = await supabase.rpc('auth_resolve_email', { identifier: id });
        if (error || !data) throw new Error('Usuario no encontrado');
        email = data;
      }
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset`
      });
      if (error) throw error;
      push('Te enviamos un enlace para restablecer tu contraseña', { type: 'success' });
    } catch (err) {
      push(err?.message || 'No se pudo iniciar el proceso de recuperación', { type: 'error' });
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 relative overflow-hidden"
    >
      {/* Soft gradient blobs background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 opacity-10 dark:opacity-5 animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-gradient-to-tr from-purple-400 to-pink-400 opacity-10 dark:opacity-5 animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <div className={`
        login-glass w-full max-w-md rounded-2xl p-8 space-y-6
        transition-all duration-700 transform relative overflow-hidden
        ${mounted ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}
      `}>
        {/* Liquid glass background */}
        <div className="liquid-bg" aria-hidden />
        {/* Logo/Icon with animation */}
        <div className="flex justify-center mb-2">
          <div className="relative">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center transform transition-transform hover:scale-110">
              <ShieldCheck className="w-8 h-8 text-white" />
            </div>
            <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-yellow-400 animate-pulse" />
          </div>
        </div>
        
        <div className="text-center">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
            Bienvenido de vuelta
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Inicia sesión para continuar con tu cuenta
          </p>
        </div>

        {rateLimitInfo && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 flex items-center gap-3 animate-slideIn">
            <Timer className="w-5 h-5 text-amber-600 dark:text-amber-400 animate-spin" />
            <div className="flex-1">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Demasiados intentos. Espera {rateLimitInfo.remaining} segundos
              </p>
              <div className="mt-1 h-1 bg-amber-200 dark:bg-amber-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-600 dark:bg-amber-400 transition-all duration-1000"
                  style={{ width: `${((rateLimitInfo.seconds - rateLimitInfo.remaining) / rateLimitInfo.seconds) * 100}%` }}
                />
              </div>
            </div>
          </div>
        )}

        <form className="space-y-5" onSubmit={doPasswordLogin}>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Usuario o correo
            </label>
            <div className={`
              relative rounded-lg border transition-all duration-300
              ${focusedInput === 'identifier' 
                ? 'border-blue-500 dark:border-blue-400' 
                : 'border-gray-300 dark:border-gray-700'
              }
            `}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className={`h-5 w-5 transition-colors ${
                  focusedInput === 'identifier' ? 'text-blue-500' : 'text-gray-400'
                }`} />
              </div>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                onFocus={() => setFocusedInput('identifier')}
                onBlur={() => setFocusedInput(null)}
                className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none"
                placeholder="usuario@ejemplo.com"
                autoComplete="username"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Contraseña
            </label>
            <div className={`
              relative rounded-lg border transition-all duration-300
              ${focusedInput === 'password' 
                ? 'border-blue-500 dark:border-blue-400' 
                : 'border-gray-300 dark:border-gray-700'
              }
            `}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className={`h-5 w-5 transition-colors ${
                  focusedInput === 'password' ? 'text-blue-500' : 'text-gray-400'
                }`} />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setFocusedInput('password')}
                onBlur={() => setFocusedInput(null)}
                className="w-full pl-10 pr-12 py-2.5 rounded-lg bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none"
                placeholder="••••••••"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                aria-pressed={showPassword}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || rateLimitInfo}
            className={`
              w-full relative overflow-hidden rounded-lg px-4 py-3 font-semibold text-white
              transition-all duration-300 transform
              ${loading || rateLimitInfo 
                ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed' 
                : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:scale-[1.02] active:scale-[0.98]'
              }
            `}
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Ingresando...
                </>
              ) : (
                <>
                  Iniciar sesión
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </span>
            {!loading && !rateLimitInfo && (
              <div className="absolute inset-0 -translate-x-full hover:translate-x-0 bg-white/10 transition-transform duration-300" />
            )}
          </button>
        </form>

        <div className="relative flex items-center gap-3 py-2">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-700 to-transparent" />
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 px-2">o continúa con</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-700 to-transparent" />
        </div>

        <div className="space-y-3">
          <button
            type="button"
            onClick={doGoogle}
            disabled={loading || rateLimitInfo}
            className="w-full group relative overflow-hidden rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 transition-all duration-300 hover:border-gray-400 dark:hover:border-gray-600 disabled:opacity-50"
          >
            <span className="relative z-10 flex items-center justify-center gap-3">
              <img alt="Google" src="https://www.google.com/favicon.ico" className="w-5 h-5" />
              <span className="text-gray-700 dark:text-gray-200 font-medium">Continuar con Google</span>
            </span>
            <div className="absolute inset-0 -translate-x-full group-hover:translate-x-0 bg-gray-50 dark:bg-gray-700/30 transition-transform duration-300" />
          </button>
          
          <button
            type="button"
            onClick={doOtp}
            disabled={loading || rateLimitInfo}
            className="w-full group relative overflow-hidden rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2.5 transition-all duration-300 hover:border-gray-400 dark:hover:border-gray-600 disabled:opacity-50"
          >
            <span className="relative z-10 flex items-center justify-center gap-3">
              <Mail className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <span className="text-gray-700 dark:text-gray-200 font-medium">Enviarme enlace de acceso</span>
            </span>
            <div className="absolute inset-0 -translate-x-full group-hover:translate-x-0 bg-gray-50 dark:bg-gray-700/30 transition-transform duration-300" />
          </button>
          
          <button
            type="button"
            onClick={doForgot}
            disabled={loading || rateLimitInfo}
            className="w-full text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline transition-colors disabled:opacity-50"
          >
            ¿Olvidaste tu contraseña?
          </button>
        </div>
      </div>
    </div>
  );
}


